import { useState, useMemo } from 'react'
import {
  Mail,
  MailOpen,
  ArrowDownLeft,
  ArrowUpRight,
  Paperclip,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Calendar,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DatePicker } from '@/components/ui/date-picker'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  useEmailsByContact,
  useEmailsByAccount,
  useEmailsByOpportunity,
  useEmailsByLead,
} from '@/hooks/use-api'
import type { Email } from '@/types'
import { toDisplayDate } from '@/lib/utils'

type DatePreset = 'all' | 'this_week' | 'last_week' | 'this_month' | 'last_month' | 'custom'

function getDateRange(preset: DatePreset): { from_date?: string; to_date?: string } {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

  switch (preset) {
    case 'this_week': {
      const dayOfWeek = today.getDay()
      const monday = new Date(today)
      monday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1))
      return { from_date: monday.toISOString().split('T')[0] }
    }
    case 'last_week': {
      const dayOfWeek = today.getDay()
      const thisMonday = new Date(today)
      thisMonday.setDate(today.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1))
      const lastMonday = new Date(thisMonday)
      lastMonday.setDate(thisMonday.getDate() - 7)
      const lastSunday = new Date(thisMonday)
      lastSunday.setDate(thisMonday.getDate() - 1)
      lastSunday.setHours(23, 59, 59)
      return {
        from_date: lastMonday.toISOString().split('T')[0],
        to_date: lastSunday.toISOString().split('T')[0] + 'T23:59:59',
      }
    }
    case 'this_month': {
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1)
      return { from_date: firstDay.toISOString().split('T')[0] }
    }
    case 'last_month': {
      const firstDay = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      const lastDay = new Date(now.getFullYear(), now.getMonth(), 0)
      lastDay.setHours(23, 59, 59)
      return {
        from_date: firstDay.toISOString().split('T')[0],
        to_date: lastDay.toISOString().split('T')[0] + 'T23:59:59',
      }
    }
    default:
      return {}
  }
}

interface RelatedEmailsProps {
  entityType: 'contact' | 'account' | 'opportunity' | 'lead'
  entityId: string
  maxHeight?: string
}

export function RelatedEmails({ entityType, entityId, maxHeight = '400px' }: RelatedEmailsProps) {
  const [expandedEmail, setExpandedEmail] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const limit = 10
  const [datePreset, setDatePreset] = useState<DatePreset>('all')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')

  const dateParams = useMemo(() => {
    if (datePreset === 'custom') {
      return {
        ...(customFrom ? { from_date: customFrom } : {}),
        ...(customTo ? { to_date: customTo + 'T23:59:59' } : {}),
      }
    }
    return getDateRange(datePreset)
  }, [datePreset, customFrom, customTo])

  const queryParams = { page, limit, ...dateParams }

  // Use the appropriate hook based on entity type
  const contactQuery = useEmailsByContact(entityType === 'contact' ? entityId : '', queryParams)
  const accountQuery = useEmailsByAccount(entityType === 'account' ? entityId : '', queryParams)
  const opportunityQuery = useEmailsByOpportunity(
    entityType === 'opportunity' ? entityId : '',
    queryParams
  )
  const leadQuery = useEmailsByLead(entityType === 'lead' ? entityId : '', queryParams)

  // Get the appropriate query result
  const query =
    entityType === 'contact'
      ? contactQuery
      : entityType === 'account'
        ? accountQuery
        : entityType === 'opportunity'
          ? opportunityQuery
          : leadQuery

  const { data, isLoading, error, refetch, isFetching } = query

  const emails = data?.data || []
  const totalEmails = data?.total || 0
  const totalPages = Math.ceil(totalEmails / limit)

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return ''
    const date = new Date(dateStr)
    const now = new Date()
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24))

    if (diffDays === 0) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    } else if (diffDays < 7) {
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        hour: 'numeric',
        minute: '2-digit',
      })
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    }
  }

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength) + '...'
  }

  const getPreviewText = (email: Email) => {
    if (email.text_body) {
      return truncateText(email.text_body.replace(/\s+/g, ' ').trim(), 120)
    }
    if (email.html_body) {
      // Strip HTML tags for preview
      const text = email.html_body
        .replace(/<[^>]*>/g, '')
        .replace(/\s+/g, ' ')
        .trim()
      return truncateText(text, 120)
    }
    return 'No preview available'
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Mail className="h-4 w-4" aria-hidden="true" />
            Related Emails
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8" role="status" aria-live="polite">
            <div
              className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"
              aria-label="Loading emails"
            ></div>
            <span className="ml-2 text-sm text-muted-foreground">Loading emails...</span>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Mail className="h-4 w-4" aria-hidden="true" />
            Related Emails
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-destructive py-4" role="alert">
            Failed to load emails. Please try again.
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Mail className="h-4 w-4" aria-hidden="true" />
            Related Emails
            {totalEmails > 0 && (
              <Badge variant="secondary" className="ml-2">
                {totalEmails}
              </Badge>
            )}
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
            aria-label="Refresh emails"
          >
            <RefreshCw
              className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`}
              aria-hidden="true"
            />
          </Button>
        </div>
        {/* Date Range Filter */}
        <div className="pt-2">
          <div className="flex flex-wrap items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground flex-shrink-0" aria-hidden="true" />
            {(
              [
                'all',
                'this_week',
                'last_week',
                'this_month',
                'last_month',
                'custom',
              ] as DatePreset[]
            ).map((preset) => {
              const labels: Record<DatePreset, string> = {
                all: 'All',
                this_week: 'This Week',
                last_week: 'Last Week',
                this_month: 'This Month',
                last_month: 'Last Month',
                custom: 'Custom',
              }
              return (
                <Button
                  key={preset}
                  variant={datePreset === preset ? 'default' : 'outline'}
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => {
                    setDatePreset(preset)
                    setPage(1)
                  }}
                  aria-pressed={datePreset === preset}
                  aria-label={`Filter emails: ${labels[preset]}`}
                >
                  {labels[preset]}
                </Button>
              )
            })}
          </div>
          {datePreset === 'custom' && (
            <div className="flex items-center gap-2 mt-2">
              <div className="flex items-center gap-1">
                <Label htmlFor="email-from-date" className="text-xs text-muted-foreground">
                  From
                </Label>
                <DatePicker
                  id="email-from-date"
                  value={customFrom}
                  onChange={(v) => {
                    setCustomFrom(v ?? '')
                    setPage(1)
                  }}
                  className="h-7 text-xs w-36"
                  aria-label="Filter from date"
                />
              </div>
              <div className="flex items-center gap-1">
                <Label htmlFor="email-to-date" className="text-xs text-muted-foreground">
                  To
                </Label>
                <DatePicker
                  id="email-to-date"
                  value={customTo}
                  onChange={(v) => {
                    setCustomTo(v ?? '')
                    setPage(1)
                  }}
                  className="h-7 text-xs w-36"
                  aria-label="Filter to date"
                />
              </div>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {emails.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <MailOpen className="h-8 w-8 mx-auto mb-2 opacity-50" aria-hidden="true" />
            <p className="text-sm">No emails found for this {entityType}.</p>
            <p className="text-xs mt-1">Emails are automatically linked when synced from Gmail.</p>
          </div>
        ) : (
          <>
            <ScrollArea style={{ maxHeight }} className="pr-4">
              <ul className="space-y-3" aria-label={`Emails related to this ${entityType}`}>
                {emails.map((email: Email) => (
                  <li key={email.id}>
                    <button
                      type="button"
                      className="w-full text-left border rounded-lg p-3 hover:bg-muted/50 transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                      onClick={() => setExpandedEmail(expandedEmail === email.id ? null : email.id)}
                      aria-expanded={expandedEmail === email.id}
                      aria-controls={`email-content-${email.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-0.5">
                          {email.incoming ? (
                            <ArrowDownLeft
                              className="h-4 w-4 text-[#183E6A]"
                              aria-label="Incoming email"
                            />
                          ) : (
                            <ArrowUpRight
                              className="h-4 w-4 text-[#2E7D5B]"
                              aria-label="Outgoing email"
                            />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-sm font-medium truncate">
                              {email.incoming
                                ? email.from_name || email.from_address
                                : email.to_address}
                            </p>
                            <span className="text-xs text-muted-foreground flex-shrink-0">
                              {formatDate(email.message_date)}
                            </span>
                          </div>
                          <p className="text-sm font-medium text-foreground truncate">
                            {email.subject || '(No subject)'}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                            {getPreviewText(email)}
                          </p>
                          <div className="flex items-center gap-2 mt-2">
                            {email.has_attachment && (
                              <Badge variant="outline" className="text-xs py-0 h-5">
                                <Paperclip className="h-3 w-3 mr-1" aria-hidden="true" />
                                Attachment
                              </Badge>
                            )}
                            {email.contact_name && (
                              <Badge variant="secondary" className="text-xs py-0 h-5">
                                {email.contact_name}
                              </Badge>
                            )}
                            <span className="flex-1"></span>
                            {expandedEmail === email.id ? (
                              <ChevronUp
                                className="h-4 w-4 text-muted-foreground"
                                aria-hidden="true"
                              />
                            ) : (
                              <ChevronDown
                                className="h-4 w-4 text-muted-foreground"
                                aria-hidden="true"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </button>

                    {/* Expanded email content */}
                    {expandedEmail === email.id && (
                      <div
                        id={`email-content-${email.id}`}
                        className="mt-2 ml-7 p-3 bg-muted/30 rounded-md border"
                      >
                        <div className="space-y-2 text-sm">
                          <div className="flex gap-2">
                            <span className="text-muted-foreground font-medium w-12">From:</span>
                            <span>
                              {email.from_name
                                ? `${email.from_name} <${email.from_address}>`
                                : email.from_address}
                            </span>
                          </div>
                          <div className="flex gap-2">
                            <span className="text-muted-foreground font-medium w-12">To:</span>
                            <span>{email.to_address}</span>
                          </div>
                          {email.cc_address && (
                            <div className="flex gap-2">
                              <span className="text-muted-foreground font-medium w-12">Cc:</span>
                              <span>{email.cc_address}</span>
                            </div>
                          )}
                          <div className="flex gap-2">
                            <span className="text-muted-foreground font-medium w-12">Date:</span>
                            <span>
                              {email.message_date &&
                                (
                                  toDisplayDate(email.message_date) ?? new Date(email.message_date)
                                ).toLocaleString('en-US', {
                                  weekday: 'long',
                                  year: 'numeric',
                                  month: 'long',
                                  day: 'numeric',
                                  hour: 'numeric',
                                  minute: '2-digit',
                                })}
                            </span>
                          </div>
                          <hr className="my-3" />
                          <div className="whitespace-pre-wrap text-sm">
                            {email.text_body ||
                              (email.html_body ? (
                                <span className="text-muted-foreground italic">
                                  HTML content - open in email client to view
                                </span>
                              ) : (
                                <span className="text-muted-foreground italic">No content</span>
                              ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </ScrollArea>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <span className="text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1 || isFetching}
                    aria-label="Previous page"
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages || isFetching}
                    aria-label="Next page"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
